import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartFrame, ChartTooltip, type TableColumn } from "./chart-frame";
import { AXIS_PROPS, CHROME, GRID_PROPS, MARK, MAX_SERIES, seriesColor } from "./tokens";

export interface TrendSeries {
  key: string;
  label: string;
  color?: string;
}

interface TrendChartProps {
  title: string;
  note?: string;
  data: Record<string, string | number>[];
  xKey: string;
  series: TrendSeries[];
  loading?: boolean;
  error?: string | null;
  height?: number;
  className?: string;
  emptyBody?: string;
  /** A single series may carry a 10%-opacity wash under the line. */
  area?: boolean;
  valueSuffix?: string;
}

/**
 * Change over time. One y-axis only: two measures of different scale get two
 * charts or an indexed common base, never a second axis — a dual-axis plot
 * invents a correlation the data does not contain.
 */
export function TrendChart({
  title,
  note,
  data,
  xKey,
  series,
  loading,
  error,
  height = 240,
  className,
  emptyBody,
  area,
  valueSuffix = "",
}: TrendChartProps) {
  const usable = series.slice(0, MAX_SERIES);
  const colorFor = (s: TrendSeries, i: number) => s.color ?? seriesColor(i);

  const columns: TableColumn[] = [
    { key: xKey, header: "Period" },
    ...usable.map((s) => ({ key: s.key, header: s.label, numeric: true })),
  ];
  const rows = data.map((row) => {
    const out: Record<string, string> = { [xKey]: String(row[xKey] ?? "") };
    for (const s of usable) out[s.key] = String(row[s.key] ?? 0);
    return out;
  });

  const legend = usable.map((s, i) => ({ label: s.label, color: colorFor(s, i) }));
  const singleSeries = usable.length === 1;

  return (
    <ChartFrame
      title={title}
      {...(note ? { note } : {})}
      legend={legend}
      table={{ columns, rows }}
      {...(loading ? { loading } : {})}
      {...(error ? { error } : {})}
      empty={!loading && !error && data.length === 0}
      {...(emptyBody ? { emptyBody } : {})}
      height={height}
      {...(className ? { className } : {})}
    >
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: -16 }}>
          <defs>
            {usable.map((s, i) => (
              <linearGradient key={s.key} id={`wash-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                {/* Not a decorative gradient: this is the flat 10% area wash the
                    mark spec calls for, expressed as two identical stops so it
                    reads as a single tint rather than a fade. */}
                <stop offset="0%" stopColor={colorFor(s, i)} stopOpacity={MARK.areaOpacity} />
                <stop offset="100%" stopColor={colorFor(s, i)} stopOpacity={MARK.areaOpacity} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey={xKey} {...AXIS_PROPS} />
          <YAxis {...AXIS_PROPS} allowDecimals={false} axisLine={false} />
          <Tooltip
            cursor={{ stroke: CHROME.axis, strokeWidth: 1 }}
            content={<ChartTooltip valueSuffix={valueSuffix} />}
          />
          {area && singleSeries && usable[0] && (
            <Area
              type="monotone"
              dataKey={usable[0].key}
              name={usable[0].label}
              stroke="none"
              fill={`url(#wash-${usable[0].key})`}
              isAnimationActive={false}
            />
          )}
          {usable.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={colorFor(s, i)}
              strokeWidth={MARK.lineWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              /* Data marks do not animate in. A 1.5s draw-on is decorative
                 movement on informational content, and it also means a chart
                 reads as empty for the first second after every navigation. */
              isAnimationActive={false}
              /* Markers carry a 2px ring in the surface colour so they stay
                 legible where lines cross. Hit area is widened for hover. */
              dot={{
                r: MARK.dotRadius,
                fill: colorFor(s, i),
                stroke: CHROME.surface,
                strokeWidth: MARK.gap,
              }}
              activeDot={{
                r: MARK.dotRadius + 2,
                fill: colorFor(s, i),
                stroke: CHROME.surface,
                strokeWidth: MARK.gap,
              }}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}
